import React, { useState } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';
import {
  Button,
  Dialog,
  ArrowLeftLg,
  Stack,
  P,
  Divider,
  Inline,
  spacingHelper,
} from '@construct-kit/core';
import styled, { css } from 'styled-components';
import {
  containerClassName,
  dangerouslySetClassNamesMultiAutocomplete,
  modalLocationSuggestStyles,
} from './locationSearchStyles';
import { ListingLocationSuggest } from './ListingLocationSuggest';
import type { SelectedFiltersOptionsType } from '../model/constants/filterTypes';
import AllFilters from './AllFilters';
import { buildLocationSearchBtnLabel } from '../locationSearch';
import SingleLocationSuggest from './SingleLocationSuggest';
import { getCustomerRecentLocations } from '../customerRecentSearches';
import { modalSingleLocationSuggestStyles } from './singleLocationSearchStyles';

const StyledDialog = styled(Dialog)<{
  isForSingleLocation: boolean;
}>`
  ${({ isForSingleLocation }) =>
    (isForSingleLocation
      ? css`
      .${modalSingleLocationSuggestStyles}`
      : css`
      .${modalLocationSuggestStyles}
      `)}

  overflow: hidden;

  .closeButton {
    top: ${spacingHelper('medium')};
  }
`;

const StyledButton = styled(Button)`
  border-radius: 1.5rem;
`;

const StyledArrowButton = styled(Button)`
  padding: 0;
`;

const StyledContainer = styled(Stack)`
  overflow: hidden;
`;

const StyledLocationText = styled.div`
  margin: 0;
  flex-basis: 100%;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;
type ButtonProp = { onClick: () => void; shouldDisableButton?: boolean };

const DoneButton = ({ onClick, shouldDisableButton }: ButtonProp) => (
  <StyledButton
    sizeVariant="medium"
    variant="secondary"
    fullWidth
    onClick={onClick}
    disabled={shouldDisableButton}
    aria-label="Confirm search location"
  >
    Done
  </StyledButton>
);

const SearchButton = ({ onClick }: ButtonProp) => (
  <StyledButton sizeVariant="medium" fullWidth onClick={onClick} aria-label="Execute search">
    Search
  </StyledButton>
);

const BackButton = ({ onClick }: ButtonProp) => (
  <StyledArrowButton
    icon={<ArrowLeftLg />}
    variant="naked"
    hideLabel
    onClick={onClick}
    aria-label="Back to previous options"
  >
    Close
  </StyledArrowButton>
);

export type LocationSuggestPopupProps = {
  onClose: () => void;
  selectedFiltersOptions: SelectedFiltersOptionsType;
  initialFilterOptions: SelectedFiltersOptionsType;
  setSelectedFiltersOptions: (value: SelectedFiltersOptionsType) => void;
  handleSearchOnClick: (onClose?: () => void) => void;
  isIncludingFilterDialog?: boolean;
  isForSingleLocation?: boolean;
};

export const SmallScreenLocationSuggestDialog = ({
  onClose,
  selectedFiltersOptions,
  initialFilterOptions,
  setSelectedFiltersOptions,
  handleSearchOnClick,
  isIncludingFilterDialog = false,
  isForSingleLocation = false,
}: LocationSuggestPopupProps): ReactElement => {
  const [showFilters, setShowFilters] = useState(false);

  const executeSearch = () => {
    handleSearchOnClick();
    onClose();
  };

  const onDoneClick = () => {
    if (!isIncludingFilterDialog) {
      executeSearch();
    } else {
      setShowFilters(true);
    }
  };

  const onBackBtnClick = () => {
    if (showFilters) {
      setShowFilters(false);
    } else {
      onClose();
    }
  };

  const handleKeyPressOnLocationSuggest = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onDoneClick();
    }
  };

  const selectedSearchLocationIsEmpty =
    !selectedFiltersOptions.searchLocations || selectedFiltersOptions.searchLocations?.length === 0;

  // eslint-disable-next-line react/no-unstable-nested-components
  const LocationSuggestSearchBar = () =>
    (isForSingleLocation ? (
      <SingleLocationSuggest
        selectedFiltersOptions={selectedFiltersOptions}
        setSelectedFiltersOptions={setSelectedFiltersOptions}
        recentLocations={getCustomerRecentLocations()}
        icon={<BackButton onClick={onBackBtnClick} />}
      />
    ) : (
      <ListingLocationSuggest
        selectedFiltersOptions={selectedFiltersOptions}
        setSelectedFiltersOptions={setSelectedFiltersOptions}
        className={containerClassName}
        dangerouslySetClassNames={dangerouslySetClassNamesMultiAutocomplete}
        shouldHideSwitch={showFilters}
        icon={<BackButton onClick={onBackBtnClick} />}
        onKeyPress={handleKeyPressOnLocationSuggest}
      />
    ));

  return (
    <StyledDialog
      noMargins
      autoFullScreen
      isForSingleLocation={isForSingleLocation}
      fullScreenMediaQuery="(max-width: 47.9375rem)"
      footer={
        showFilters ? (
          <SearchButton onClick={executeSearch} />
        ) : (
          <DoneButton
            onClick={onDoneClick}
            shouldDisableButton={isForSingleLocation && selectedSearchLocationIsEmpty}
          />
        )
      }
      onClose={showFilters ? onClose : undefined}
      dangerouslySetClassNames={{
        closeButton: 'closeButton',
      }}
    >
      <StyledContainer gap="0">
        {!showFilters ? (
          <LocationSuggestSearchBar />
        ) : (
          <Inline gap="0" inset="small extraSmall small 0">
            <BackButton onClick={onBackBtnClick} />
            <StyledLocationText onClick={onBackBtnClick}>
              <P>{buildLocationSearchBtnLabel(selectedFiltersOptions.searchLocations)}</P>
            </StyledLocationText>
          </Inline>
        )}

        {isIncludingFilterDialog && showFilters && (
          <>
            <StyledDivider />
            <AllFilters
              initialFilterOptions={initialFilterOptions}
              selectedFiltersOptions={selectedFiltersOptions}
              setSelectedFiltersOptions={setSelectedFiltersOptions}
            />
          </>
        )}
      </StyledContainer>
    </StyledDialog>
  );
};
